// =============================================================================
// domain/leakExtension.ts — what holding on would have been worth.
// -----------------------------------------------------------------------------
// ── 🚨 THE NAIVE VERSION OF THIS NUMBER IS WRONG BY MORE THAN 2× ──
//
// "Sum the favourable runs after each exit" is the obvious implementation and it
// is a fantasy, in two separate ways the old client found on real data:
//
//   • a trade that ran your way and THEN reversed into your stop keeps the peak
//     of every bar before the stop bar — the head-fake scored as money you left
//     behind, when holding would in fact have taken a FULL stop-loss;
//   • a trade stopped on the very first bar scores exactly $0 — again, when
//     holding would have cost you the stop.
//
// On the old client's own book that inflated the leak by over 2× and hid the
// fact that ~79% of early exits were PROTECTIVE. So this credits the favourable
// continuation on exits that never touched the stop and SUBTRACTS the stop-loss
// on the ones that reversed. The net is the only figure that is not a fantasy,
// and it can be NEGATIVE — holding longer loses money, there is no leak — which
// is why the leak declares `floor: 0` and refuses to baseline it.
//
// ── ⚠ THIS READS A STORED WALK. IT DOES NOT WALK BARS ──
//
// The bar walk costs money (1-second bars, per trade, from a metered vendor) and
// runs in the old client behind a Recompute button. What it leaves behind is
// `extras.extensions['30']`, and that is what this reads. Nothing here fetches
// anything, so a trade whose extension was never computed is simply absent from
// the population — it does not read as a zero.
//
// ── 🚨 THE DOLLARS-PER-POINT IS DERIVED FROM THE STORED PAIR ──
//
// `dollars` was written as `points × dollarsPerPoint` in the same expression, so
// their ratio IS that instrument's dollars-per-point, exactly. Reading it back
// out means this leak can never disagree with the extension the old client
// computed, on every trade whose run was not exactly zero.
//
// ⚠ AND THE STORED `dollars` IS PER CONTRACT — `computeTradeExtension` omits
// quantity where the read-side `excursionValue` multiplies by it. Using the
// stored figure directly would under-report every multi-lot trade by its own
// size, silently and in the flattering direction.
//
// 🚨 THE RATIO ALONE IS NOT ENOUGH, AND THE CASE IT MISSES IS THE IMPORTANT ONE.
// A trade stopped on the very FIRST bar has its peak set back to the exit — so
// its run is exactly 0 points and 0 dollars, and `0 / 0` is not a price. That is
// precisely one of the two cases this leak exists to charge for. So the ratio is
// preferred (it is exact, and it guarantees the two clients agree) and the
// contract catalogue is the fallback, the same source the old client uses for
// every trade.
// =============================================================================

import { dollarsPerPointFor, type ContractSpec } from './trade';

/** The window the old client's readers use. Its key in `extensions` is a string. */
export const EXTENSION_WINDOW = '30';

/**
 * One LEG of a stored walk — the run measured against one stop.
 *
 * ⚠ `rMultiple` IS STORED, not derived here. It was written as
 * `points / riskPoints` at compute time, so reading it back guarantees this
 * client and the old one cannot disagree about a trade's R — and it is `null`
 * for a trade that had no risk to normalise against, which is a different fact
 * from zero.
 */
export interface ExtensionLeg {
  points?: number | null;
  dollars?: number | null;
  rMultiple?: number | null;
  /** True when holding would have hit that leg's stop before the peak. */
  stoppedFirst?: boolean | null;
}

/** One window's stored walk. Field names match `computeTradeExtension` exactly. */
export interface StoredExtension extends ExtensionLeg {
  /** False when no stop was derivable — the walk fell back to the raw peak. */
  qualified?: boolean | null;
  /**
   * How the trade ended, classified at COMPUTE time and stamped on the
   * top-level object only — never on `be`.
   *
   * ⚠ READ, NEVER RE-DERIVED. The old client classifies against the planned
   * target and stop at the moment it walks the bars; re-classifying here from
   * today's row would relabel a trade whose target was edited afterwards, and
   * the card's own filter chips would then disagree with the walk they filter.
   */
  exitReason?: string | null;
  /** The same walk qualified against ENTRY, for trades with no stop. */
  be?: ExtensionLeg | null;
}

/** Which stop the run is credited against. */
export type ExtensionLens = 'stop' | 'be';

/**
 * The LEG this trade is allowed to be read from, under the active lens.
 *
 * ⚠ THE `stop` LENS IS NOT "always the top-level object". Where no stop was
 * derivable the top level holds an UNQUALIFIED raw peak — a run measured against
 * a position nobody was holding — so it falls through to the break-even leg
 * instead. That fallthrough is the whole no-raw-peak rule, and it is why a
 * stop-less trade still appears rather than silently leaving the population.
 */
export function qualifiedExtLeg(
  ext: StoredExtension | null | undefined, lens: ExtensionLens = 'stop',
): ExtensionLeg | null {
  if (!ext) return null;
  if (lens === 'be') return ext.be ?? null;
  return ext.qualified === true ? ext : (ext.be ?? null);
}

export interface ExtensionTrade {
  contract?: string | null;
  side: string | null;
  entry: number | null;
  exit: number | null;
  qty: number | null;
  riskPoints: number | null;
  plannedStop?: number | null;
  extensions?: Record<string, StoredExtension> | null;
}

/**
 * 🚨 `== null` AND `''` FIRST. `Number(null)` is **0**, not NaN, and `Number('')`
 * is 0 too — so the `Number.isFinite(Number(x))` idiom that reads like a null
 * guard passes both. This file had that defect in four places at once:
 *
 *   • a walk with a null point distance was credited as a real zero-point run
 *     rather than excluded from the population;
 *   • a trade with no recorded SIZE got `qty = 0`, so it contributed exactly
 *     nothing to the premature-exit leak — silently, and in the flattering
 *     direction — while the `?? 1` fallback next to it never fired, because 0 is
 *     not null;
 *   • a trade with no ENTRY had its break-even hold price read as price 0, which
 *     charges the whole exit price back as the cost of holding;
 *   • the same for a missing exit.
 *
 * The Coach's Report's premature-exit leak reads every one of those. Found by a
 * fixture built to make the right and wrong answers different — not by types, and
 * not by any of the existing tests.
 */
const fin = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const extensionOf = (t: ExtensionTrade): StoredExtension | null => {
  const e = t?.extensions?.[EXTENSION_WINDOW];
  return (e && typeof e === 'object') ? e : null;
};

/**
 * The run this trade is allowed to be credited with, in points.
 *
 * ⚠ NEVER THE RAW PEAK. Josh's own hard rule in the old client: credit the
 * stop-qualified run where a real stop existed, else the break-even-qualified
 * run measured against entry — but never an unqualified peak, which assumes a
 * position nobody was holding.
 */
export function qualifiedExtPoints(ext: StoredExtension | null): number | null {
  // ⚠ ONE implementation of the rule, shared with the Trade Extension card. Two
  // readers of "which run counts" would eventually disagree, and the trader
  // would be shown one figure on the cost card and another on the card next to
  // it describing the same walk.
  const leg = qualifiedExtLeg(ext, 'stop');
  return leg ? fin(leg.points) : null;
}

/**
 * That instrument's dollars per point, recovered from the stored pair.
 *
 * ⚠ Falls back to the CATALOGUE when the ratio is unusable — a zero-point run
 * carries a zero-dollar figure and `0 / 0` is not a price. Without the fallback
 * a trade stopped on its first bar would leave the population, and that is one
 * of the two cases this leak exists to charge for. Null only when neither
 * source can price it; the trade is then UNPRICED and counted as such.
 */
function dollarsPerPoint(
  ext: StoredExtension, t: ExtensionTrade, catalogue: readonly ContractSpec[],
): number | null {
  for (const leg of [ext, ext.be]) {
    if (!leg) continue;
    const p = fin(leg.points); const d = fin(leg.dollars);
    if (p !== null && d !== null && p !== 0) {
      const dpp = d / p;
      if (Number.isFinite(dpp) && dpp > 0) return dpp;
    }
  }
  // ⚠ THE FALLBACK CARRIES THE FIRST-BAR STOP. See the header — a run of exactly
  // zero points is what a trade stopped immediately looks like, and dropping it
  // for want of a multiplier would silently forgive the very case the leak is
  // for.
  const cat = dollarsPerPointFor(t.contract ?? null, catalogue);
  return cat > 0 ? cat : null;
}

/**
 * The price holding would have taken you out at.
 *
 * ⚠ MIRRORS THE WALK'S OWN STOP DERIVATION — explicit planned stop, else
 * entry ∓ risk. On the break-even leg the walk terminated at ENTRY, so entry is
 * the price holding produced. Deriving it any other way here would charge back a
 * different stop from the one the walk actually simulated.
 */
function holdExitPrice(t: ExtensionTrade, usedBe: boolean): number | null {
  const entry = fin(t.entry);
  if (usedBe) return entry;
  const ps = fin(t.plannedStop);
  if (ps !== null && ps > 0) return ps;
  const rp = fin(t.riskPoints);
  if (entry !== null && rp !== null && rp > 0) {
    return t.side === 'long' ? entry - rp : entry + rp;
  }
  return null;
}

export interface PrematureExit {
  /** ⚠ SIGNED. Positive = money left behind. Negative = holding would have COST you. */
  value: number;
  stopped: boolean;
  /** The qualified run in points, for the review column. */
  points: number;
  /**
   * The same quantity as `value`, in POINTS and with the same sign.
   *
   * 🚨 NOT `points` ABOVE. In the stopped branch `points` is the qualified
   * run while `value` is built from the hold-to-stop distance — a different
   * leg entirely. A caller converting to another unit via `points` would
   * price the wrong move and produce a plausible number.
   *
   * ⚠ Per contract, like every other points figure here: `value` multiplies
   * by `$/point` AND quantity, and this is what it multiplies.
   */
  pointsValue: number;
}

/**
 * What holding this trade instead of exiting would have been worth, in dollars.
 *
 * `null` when it cannot be priced — no extension, nothing qualifiable, or no
 * derivable stop. Not zero: a trade nobody could measure and a trade that was
 * worth nothing are different facts, and the second is the one that would make
 * a trader conclude their exits are already perfect.
 */
export function prematureExitValue(
  t: ExtensionTrade, catalogue: readonly ContractSpec[] = [],
): PrematureExit | null {
  const ext = extensionOf(t);
  if (!ext) return null;

  const qpts = qualifiedExtPoints(ext);
  if (qpts === null) return null;

  const dpp = dollarsPerPoint(ext, t, catalogue);
  if (dpp === null) return null;
  const qty = fin(t.qty) ?? 1;

  // ⚠ MIRRORS `qualifiedExtPoints`: an unqualified walk means the break-even leg
  // is the one that was credited, so the break-even leg is the one whose stop
  // flag applies. Reading `ext.stoppedFirst` here regardless would charge a stop
  // that the credited walk never simulated.
  const usedBe = ext.qualified !== true;
  const stopped = usedBe ? !!ext.be?.stoppedFirst : !!ext.stoppedFirst;

  if (!stopped) {
    const pv = Math.max(0, qpts);
    return { value: pv * dpp * qty, stopped: false, points: qpts, pointsValue: pv };
  }

  const px = holdExitPrice(t, usedBe);
  const exit = fin(t.exit);
  if (px === null || exit === null) return null;   // can't price it → can't count it
  const d = t.side === 'long' ? exit - px : px - exit;
  // ⚠ NEGATIVE, because this is what holding would have COST — but never
  // NEGATIVE ZERO. `-(0)` is `-0` in JavaScript, which is not `0` to a strict
  // comparison and prints as "-$0". This session has already shipped one
  // `−0.00R` for the same reason.
  const costPts = Math.max(0, d);
  const cost = costPts * dpp * qty;
  // ⚠ Same `-0` guard as `value`, for the same reason.
  return {
    value: cost === 0 ? 0 : -cost,
    stopped: true,
    points: qpts,
    pointsValue: costPts === 0 ? 0 : -costPts,
  };
}

export interface PrematureExitNet {
  /** favourable − stopCost. ⚠ Can be negative; there is then no leak. */
  net: number;
  favorable: number;
  stopCost: number;
  /** Exits that ran favourably without hitting the stop — the review set's size. */
  favN: number;
  stoppedN: number;
  /** Measurable run, unpriceable stop. Counted so it is not mistaken for a clean trade. */
  unpriced: number;
  population: number;
  stoppedPct: number | null;
}

/**
 * The stop-aware net across a book.
 *
 * ⚠ STILL AN UPPER BOUND, IN BOTH LEGS, and deliberately:
 *   • `favorable` assumes you would have exited at the exact post-exit peak.
 *   • `stopCost` is a LOWER bound — `Math.max(0, d)` zeroes trades already exited
 *     past their stop, and the break-even leg only charges back to entry.
 *
 * ⚠ `stoppedPct` TRAVELS WITH THE DOLLARS. A naked "$27,990 left behind" invites
 * a naive hold-longer rule; "…and 79% of your exits were protective" is the same
 * fact told honestly, and it is the single most dangerous number in the report
 * without it.
 */
export function prematureExitNet<T extends ExtensionTrade>(
  trades: readonly T[], catalogue: readonly ContractSpec[] = [],
): PrematureExitNet & { favTrades: T[] } {
  let favorable = 0; let stopCost = 0;
  let favN = 0; let stoppedN = 0; let unpriced = 0; let population = 0;
  const favTrades: T[] = [];

  for (const t of trades) {
    const ext = extensionOf(t);
    if (!ext) continue;
    if (qualifiedExtPoints(ext) === null) continue;

    const pv = prematureExitValue(t, catalogue);
    if (!pv) { unpriced += 1; continue; }

    population += 1;
    if (pv.stopped) {
      stopCost += -pv.value;
      stoppedN += 1;
    } else if (pv.value > 0) {
      favorable += pv.value;
      favN += 1;
      favTrades.push(t);
    }
  }

  return {
    net: favorable - stopCost,
    favorable, stopCost, favN, stoppedN, unpriced, population,
    stoppedPct: population ? (stoppedN / population) * 100 : null,
    favTrades,
  };
}
